import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useStoreQuery, queries } from "@/lib/store";
import { GlassCard, Avatar, PillTag } from "./UI";
import Icon from "./Icon";

interface Recipient {
  threadId: string;
  route: string;
  name: string;
  initials: string;
  ringColor: string;
  subtitle: string;
  badge?: { text: string; tone: "purple" | "gold" | "fiber" };
  locked?: boolean;
}

/**
 * Roster of every sister Hannah can start a conversation with.
 * Includes Lydia (always available) and Coach Kiley (tier-gated). The
 * remaining sisters are pulled from the mentees ledger.
 */
function buildRoster(canCoach: boolean, mentees: any[], selfId: string | null): Recipient[] {
  const lydia: Recipient = {
    threadId: "lydia", route: "/chat/lydia",
    name: "Sister Lydia", initials: "SL",
    ringColor: "rgba(127,119,221,0.55)",
    subtitle: "Form & nourishment companion · always on",
    badge: { text: "Assistant", tone: "purple" },
  };
  const kiley: Recipient = {
    threadId: "kiley", route: canCoach ? "/kiley" : "/flourish",
    name: "Coach Kiley", initials: "KM",
    ringColor: "rgba(160,120,32,0.55)",
    subtitle: canCoach ? "Direct line to your coach" : "Rooted unlocks direct messaging",
    badge: { text: canCoach ? "Coach" : "Locked", tone: "gold" },
    locked: !canCoach,
  };
  const slugFor = (id: string) => {
    const m: Record<string, string> = {
      u_rebecca: "rebecca", u_joanna: "joanna", u_bethany: "bethany",
      u_mara: "mara", u_ruth: "ruth", u_eden: "eden",
    };
    return m[id] ?? id.replace("u_", "");
  };
  const sisters: Recipient[] = mentees
    // Drop yourself from the recipient list. This was hardcoded to the
    // seeded demo account's id, so a real sister saw herself as a
    // messageable recipient while Hannah was hidden from everyone.
    .filter((m) => m.id !== selfId)
    .map((m) => ({
      threadId: slugFor(m.id),
      route: `/chat/${slugFor(m.id)}`,
      name: m.name,
      initials: m.initials,
      ringColor:
        m.tier === "Flourishing" ? "rgba(192,120,74,0.55)" :
        m.tier === "Rooted" ? "rgba(168,212,184,0.55)" :
        "rgba(160,120,32,0.40)",
      subtitle: `${m.tier} · ${m.lastCheckIn ?? "Recently"}`,
      badge: { text: m.tier, tone: m.tier === "Seedling" ? "fiber" : "gold" },
    }));
  return [lydia, kiley, ...sisters];
}

export default function ComposeNewMessage({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. This is a permission check (Rooted and
  // Flourishing tiers may message the coach directly), so falling back to the
  // seeded account granted access based on Hannah's tier rather than hers.
  // With no known user it resolves false — fail closed.
  const canCoach = useStoreQuery(() =>
    me?.id ? queries.canMessageCoachDirectly(me.id) : false,
  );
  // Hannah's view of the sister roster: pull Coach Kiley's mentee list,
  // since they share Coach Kiley as their coach.
  const mentees = useStoreQuery(() => queries.menteesForCoach("u_kiley"));

  const roster = useMemo(
    () => buildRoster(canCoach, mentees ?? [], me?.id ?? null),
    [canCoach, mentees, me?.id],
  );
  const filtered = useMemo(() => {
    if (!query.trim()) return roster;
    const q = query.trim().toLowerCase();
    return roster.filter(
      (r) => r.name.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q),
    );
  }, [roster, query]);

  if (!open) return null;

  const pick = (r: Recipient) => {
    onClose();
    setLocation(r.route);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)" }}
      onClick={onClose}
      data-testid="overlay-compose"
    >
      <div
        className="w-full max-w-md"
        style={{
          background: "var(--mnf-cr)",
          borderRadius: "28px 28px 0 0",
          maxHeight: "82vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 -16px 40px rgba(22,56,40,0.28)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="pt-3 pb-1 flex justify-center">
          <div style={{ width: 44, height: 4, borderRadius: 2, background: "rgba(30,74,53,0.18)" }} />
        </div>

        {/* Header */}
        <div className="px-5 pt-2 pb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="mnf-eyebrow">New conversation</p>
            <button
              onClick={onClose}
              className="text-sm mnf-body"
              style={{ color: "var(--mnf-tg)" }}
              aria-label="Close compose"
              data-testid="button-compose-close"
            >
              Cancel
            </button>
          </div>
          <h2 className="mnf-serif" style={{ fontSize: 24, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.1 }}>
            Who would you like to write to?
          </h2>
          <p className="mnf-body mt-1" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
            Lydia is always on for form and nourishment questions. Coach Kiley
            replies in arrival order.
          </p>
        </div>

        {/* Search */}
        <div className="px-5 pb-3">
          <div
            className="flex items-center gap-2 px-3"
            style={{
              height: 42, borderRadius: 14,
              background: "rgba(30,74,53,0.06)",
              border: "1px solid rgba(30,74,53,0.10)",
            }}
          >
            <Icon name="search" size={18} />
            <input
              data-testid="input-compose-search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name"
              className="flex-1 outline-none bg-transparent mnf-body"
              style={{ fontSize: 14, color: "var(--mnf-fd)" }}
            />
            {query && (
              <button onClick={() => setQuery("")} aria-label="Clear search">
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Roster */}
        <div className="px-5 pb-6 overflow-y-auto" style={{ flex: 1 }} data-testid="list-compose-roster">
          {filtered.length === 0 ? (
            <div className="text-center py-10">
              <p className="mnf-body italic" style={{ color: "var(--mnf-tg)" }}>
                No sister by that name yet. Try a different search.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((r) => (
                <button
                  key={r.threadId}
                  onClick={() => pick(r)}
                  className="w-full text-left"
                  data-testid={`button-pick-${r.threadId}`}
                >
                  <GlassCard padding={12}>
                    <div className="flex items-center gap-3">
                      <Avatar initials={r.initials} size={42} ringColor={r.ringColor} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p
                            className="mnf-serif truncate"
                            style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}
                          >
                            {r.name}
                          </p>
                          {r.badge && (
                            <PillTag tone={r.badge.tone}>{r.badge.text}</PillTag>
                          )}
                        </div>
                        <p
                          className="text-xs mnf-body truncate"
                          style={{ color: r.locked ? "var(--mnf-coral)" : "var(--mnf-tg)" }}
                        >
                          {r.subtitle}
                        </p>
                      </div>
                      <Icon name={r.locked ? "lock" : "chevron_right"} size={16} />
                    </div>
                  </GlassCard>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 pb-5 pt-2" style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}>
          <p className="text-xs mnf-body italic text-center" style={{ color: "var(--mnf-tg)" }}>
            "A friend loves at all times." Proverbs 17:17
          </p>
        </div>
      </div>
    </div>
  );
}
